import camelcaseKeys from 'camelcase-keys';
import z from 'zod';

import { zodEnum } from '@shared/helpers/zod';
import { makeApiMutation, makeApiQuery } from '@shared/utils/makeApiQuery';

import { MEMBERSHIP_TIER_ID } from '@api/types';
import type { ApplicationActivitiesPayload } from '@api/types/applications';
import { ApplicationActivitiesPayloadSchema } from '@api/types/applications';

import { ApplicationValidationStatusSchema } from '@domain:applications/model';

import type { ApiOptions, ApiResponse } from '../ApiProvider';
import Api from '../ApiProvider';

interface ApplicationSocialLinkData {
  link: string;
  link_site: string;
}

export interface ApplicationCategory {
  category_name: string;
}

interface ApplicationSubcategory {
  subcategory_name: string;
}

export interface Screenshot {
  id: number;
  file: string;
  number: number;
}

interface FaqItem {
  question: string;
  answer: string;
}

export interface Ratings extends Record<1 | 2 | 3 | 4 | 5, number> {
  all: number;
  averageScoreRounded: number;
  maxValue?: number;
}

export interface AboutContent {
  introduction?: string;
  overview?: string;
  benefits?: string;
  get_started?: string;
  project_vision?: string;
  roadmap?: string;
  meet_the_team?: string;
  testnet_beta_access?: string;
  token_overview?: string;
  token_summary?: string;
  token_whitepaper?: string;
}

export interface ApplicationCreator {
  name: string;
  icon: string;
  apps_count: number;
  developer_name_id: string;
}

export interface ApplicationChain {
  chain_name: string;
  chain_short_name?: string;
  icon?: string;
  contract_link?: string;
}

export interface GetApplicationApiResponsePayload {
  id: number;
  app_id: string;
  app_name: string;
  icon: string;
  status: string;
  tags: Array<string>;
  short_description: string;
  long_description: string;
  coins: string[];
  category?: ApplicationCategory;
  subcategory?: ApplicationSubcategory;
  socials: Array<ApplicationSocialLinkData>;
  screenshots?: Array<Screenshot>;
  links?: Record<string, string>;
  faq?: Array<FaqItem>;
  likes?: number;
  production_ready: boolean;
  app_chain?: string;
  ratings?: Ratings;
  validators_score?: number;
  search_rank?: number;
  category_rank?: number;
  chain_rank?: number;
  release_date?: string;
  upcoming_date?: string;
  about?: AboutContent;
  has_active_hot_offer?: boolean;
  creator?: ApplicationCreator;
  chains?: Array<ApplicationChain>;
}

export const getApplication = async (
  applicationSlugOrId: string | number,
  options?: ApiOptions,
) => {
  const data = await Api.get(`v2/store/applications/${applicationSlugOrId}`, options).json<
    ApiResponse<GetApplicationApiResponsePayload>
  >();

  return data;
};

const VoteSchema = z.object({
  vote: z.number(),
  funny: z.boolean(),
});

const ReviewSchema = z.object({
  vote: VoteSchema,
});

const GetUserActivityPayloadSchema = z
  .object({
    suggestions: z.record(ReviewSchema),
    reviews: z.record(ReviewSchema),
    trustReviews: z.record(ReviewSchema),
    like: z.boolean(),
    vote: z.number(),
  })

export type GetUserActivityPayload = z.infer<
  typeof GetUserActivityPayloadSchema
>;

export const getUserActivity = makeApiQuery(
  async (applicationSlugOrId: string | number, options?: ApiOptions) => {
    const data = await Api.get(
      `v2/store/applications/${applicationSlugOrId}/user-activity`,
      options,
    ).json<ApiResponse<GetUserActivityPayload>>();

    return data;
  },
  (applicationSlugOrId) => ({
    queryKey: ['applications', applicationSlugOrId, 'user-activity'],
  }),
);

export const setApplicationLike = makeApiMutation(
  (applicationId: number) => async () => {
    return Api.post(`v2/store/application/${applicationId}/like`).json<{
      id: number;
      user_id: number;
      app_id: number;
    }>();
  },
  [getUserActivity],
);

export const unsetApplicationLike = makeApiMutation(
  (applicationId: number) => async () => {
    return Api.delete(`v2/store/application/${applicationId}/like`).json<{ data: string }>(); // api returns just string, but we transform it in object
  },
  [getUserActivity],
);

const ApplicationFeedbackItemDataSchema = z
  .object({
    id: z.number(),
    user_id: z.number(),
    app_id: z.number().optional(),
    app_name: z.string().optional(),
    text: z.string(),
    title: z.string(),
    create_time: z.string(),
    positive: z.number(),
    negative: z.number(),
    funny: z.number(),
    author: z.object({
      magic_id: z.string(),
      name: z.string().nullable(),
      icon: z.string(),
      membership: z.enum(zodEnum(Object.values(MEMBERSHIP_TIER_ID))),
      karma: z.number().optional(),
    }),
    rating: z.number().optional(),
  })
  .transform((value) => camelcaseKeys(value, { deep: true }));

export type ApplicationFeedbackItemData = z.infer<typeof ApplicationFeedbackItemDataSchema>;

const GetApplicationReviewsApiResponseDataSchema = ApplicationFeedbackItemDataSchema.array();

export type GetApplicationReviewsApiResponseData = z.infer<
  typeof GetApplicationReviewsApiResponseDataSchema
>;

export const getApplicationReviewsInfinite = makeApiQuery(
  async (applicationSlugOrId: string | number, params?: { limit?: number; offset?: number }) => {
    const { payload } = await Api.get(`v2/store/applications/${applicationSlugOrId}/reviews`, {
      searchParams: params,
    }).json<ApiResponse<GetApplicationReviewsApiResponseData>>();

    return GetApplicationReviewsApiResponseDataSchema.parse(payload);
  },
  (applicationSlugOrId) => ({
    queryKey: ['application', applicationSlugOrId, 'reviews', 'infinite'],
  }),
);

const GetApplicationSuggestionsApiResponseDataSchema = ApplicationFeedbackItemDataSchema.array();

export type GetApplicationSuggestionsApiResponsePayload = z.infer<
  typeof GetApplicationSuggestionsApiResponseDataSchema
>;

export const getApplicationSuggestionsInfinite = makeApiQuery(
  async (applicationSlugOrId: string | number, params?: { limit?: number; offset?: number }) => {
    const { payload } = await Api.get(`v2/store/applications/${applicationSlugOrId}/suggestions`, {
      searchParams: params,
    }).json<ApiResponse<GetApplicationSuggestionsApiResponsePayload>>();

    return GetApplicationSuggestionsApiResponseDataSchema.parse(payload);
  },
  (applicationSlug) => ({
    queryKey: ['application', applicationSlug, 'suggestions', 'infinite'],
  }),
);

export const getApplicationSuggestion = makeApiQuery( async (params: { appId: string, suggestionId: number }, options?: ApiOptions) => {
  const { payload } = await Api.get(
    `v2/store/applications/${params.appId}/suggestions/${params.suggestionId}`,
    options,
  ).json<ApiResponse<ApplicationFeedbackItemData>>();

  return ApplicationFeedbackItemDataSchema.parse(payload);
});

export const getApplicationReview = makeApiQuery (async (params: { appId: string, reviewId: number }, options?: ApiOptions) => {
  const { payload } = await Api.get(
    `v2/store/applications/${params.appId}/reviews/${params.reviewId}`,
    options,
  ).json<ApiResponse<ApplicationFeedbackItemData>>();

  return ApplicationFeedbackItemDataSchema.parse(payload);
});

const AddApplicationSuggestionRequestSchema = z.object({
  json: z.object({
    id: z.number(),
    suggestion_text: z.string(),
  }),
});

export type AddApplicationSuggestionRequest = z.infer<typeof AddApplicationSuggestionRequestSchema>;

const AddApplicationSuggestionApiResponseDataSchema = z.object({
  id: z.number(),
  user_id: z.number(),
  user_name: z.string(),
  app_id: z.number(),
  text: z.string(),
  title: z.string(),
  create_time: z.string(),
  deleted: z.boolean(),
  app_name: z.string(),
});

export type AddApplicationSuggestionApiResponseData = z.infer<
  typeof AddApplicationSuggestionApiResponseDataSchema
>;

export const addApplicationSuggestion = makeApiMutation(
  (applicationSlug: string) => async (options: { applicationId: number; payload: AddApplicationSuggestionRequest }) => {
    return Api.post(
      `v2/store/applications/${options.applicationId}/suggestions`,
      options.payload,
    ).json<AddApplicationSuggestionApiResponseData>();
  },
  [getApplicationSuggestionsInfinite],
);

const AddApplicationReviewRequestSchema = z.object({
  json: z.object({
    id: z.number(),
    review_text: z.string(),
    review_rating: z.string().nullable(),
    review_title: z.string(),
  }),
});

export type AddApplicationReviewRequest = z.infer<
  typeof AddApplicationReviewRequestSchema
>;

const AddApplicationReviewApiResponseDataSchema = z.object({
  id: z.number(),
  user_id: z.number(),
  user_name: z.string(),
  app_id: z.number(),
  text: z.string(),
  title: z.string(),
  create_time: z.string(),
  deleted: z.boolean(),
  app_name: z.string(),
});

export type AddApplicationReviewApiResponseData = z.infer<
  typeof AddApplicationReviewApiResponseDataSchema
>;

export const addApplicationReview = makeApiMutation(
  (applicationSlug: string) =>
    async (options: { applicationId: number; payload: AddApplicationReviewRequest }) => {
      return Api.post(
        `v2/store/applications/${options.applicationId}/reviews`,
        options.payload,
      ).json<AddApplicationReviewApiResponseData>();
    },
  [getApplicationReviewsInfinite],
);

const GetRecommendedApplicationsResponsePayloadSchema = z
  .object({
    app_name: z.string(),
    app_banner: z.any().nullable(),
    icon: z.string(),
    is_verified: z.boolean(),
    production_ready: z.boolean(),
    app_chain: z.any().nullable(),
    category_id: z.number(),
    google_play_link: z.any().nullable(),
    app_store_link: z.any().nullable(),
    subcategory_id: z.number(),
    link: z.string(),
    app_website: z.string().nullable(),
    devices: z.any(),
    is_adaptive: z.boolean(),
    iframe_confirm: z.boolean(),
    user_id: z.number(),
    status: ApplicationValidationStatusSchema,
    id: z.number(),
    app_id: z.string(),
    long_description: z.string(),
    short_description: z.string(),
    category: z
      .object({
        id: z.number(),
        category_name: z.string(),
        google_app_category_id: z.number(),
      })
      .transform(({ category_name, ...rest }) => ({ name: category_name, ...rest })),
    subcategory: z
      .object({
        id: z.number(),
        subcategory_name: z.string(),
      })
      .transform(({ subcategory_name, ...rest }) => ({ name: subcategory_name, ...rest })),
    has_active_hot_offer: z.boolean().optional(),
  })
  .transform((value) => camelcaseKeys(value, { deep: true }))
  .array();

type GetRecommendedApplicationsResponsePayload = z.infer<
  typeof GetRecommendedApplicationsResponsePayloadSchema
>;

export const getRecommendedApplications = makeApiQuery(
  async (applicationId: number, options?: ApiOptions) => {
    const { payload } = await Api.get(
      `v2/store/applications/${applicationId}/recommended-apps`,
      options,
    ).json<ApiResponse<GetRecommendedApplicationsResponsePayload>>();

    return GetRecommendedApplicationsResponsePayloadSchema.parse(payload);
  },
  (applicationSlugOrId) => ({
    queryKey: ['applications', applicationSlugOrId, 'user-activity'],
  }),
);

const ReviewOptionTypeSchema = z
  .object({
    id: z.string(),
    title: z.string(),
    description: z.string(),
  })
  .array();

type ReviewOptionType = z.infer<typeof ReviewOptionTypeSchema>;

export const getApplicationReviewReportTypes = makeApiQuery(
  async (options?: ApiOptions) => {
    const data = await Api.get(
      `v2/store/application/reviews/report/types`,
      options,
    ).json<ReviewOptionType>();

    return data;
  },
  () => ({
    queryKey: ['applicationReportTypes'],
  }),
);

const ReportApplicationSuggestionRequestSchema = z.object({
  suggestionId: z.number(),
  reportText: z.string(),
  reportType: z.string(),
});

type ReportApplicationSuggestionRequest = z.infer<typeof ReportApplicationSuggestionRequestSchema>;

const ReportApplicationSuggestionResponseSchema = z.object({
  reportId: z.number(),
});

type ReportApplicationSuggestionResponse = z.infer<
  typeof ReportApplicationSuggestionResponseSchema
>;

export const reportApplicationSuggestion = makeApiMutation(
  (applicationSlug: string) => async (options: ReportApplicationSuggestionRequest) => {
    const suggestionId = options.suggestionId;

    return Api.post(
      `v2/store/applications/${applicationSlug}/suggestions/${suggestionId}/report`,
      { json: options },
    ).json<ReportApplicationSuggestionResponse>();
  },
);

const ReportApplicationReviewRequestSchema = z.object({
  reviewId: z.number(),
  reportText: z.string(),
  reportType: z.string(),
});

type ReportApplicationReviewRequest = z.infer<typeof ReportApplicationReviewRequestSchema>;

const ReportApplicationReviewResponseSchema = z.object({
  reportId: z.number(),
});

type ReportApplicationReviewResponse = z.infer<typeof ReportApplicationReviewResponseSchema>;

export const reportApplicationReview = makeApiMutation(
  (applicationSlug: string) => async (options: ReportApplicationReviewRequest) => {
    const reviewId = options.reviewId;

    return Api.post(
      `v2/store/applications/${applicationSlug}/reviews/${reviewId}/report`,
      { json: options },
    ).json<ReportApplicationReviewResponse>();
  },
);

export const getApplicationActivities = makeApiQuery<
  [number, ApiOptions?],
  ApplicationActivitiesPayload
>(
  async (applicationId: number, options?: ApiOptions) => {
    const { payload } = await Api.get(
      `v2/store/applications/${applicationId}/engagements`,
      options,
    ).json<ApiResponse<ApplicationActivitiesPayload>>();

    return ApplicationActivitiesPayloadSchema.parse(payload);
  },
  (applicationId) => ({
    queryKey: ['applications', applicationId, 'engagements'],
  }),
);
